/**
 * Work Group Utilities
 *
 * Pure functions for normalizing and matching books by work group.
 * A "work group" groups all editions (hardcover, paperback, board book)
 * of the same literary work using a normalized "title::author" key.
 *
 * Extracted from book-management.ts for reuse and testability.
 */

// ============================================================================
// Title Normalization
// ============================================================================

/**
 * Normalize a book title for work group matching.
 * - Lowercase
 * - Trim whitespace
 * - Strip leading articles ("the", "a", "an") for better matching
 * - Collapse multiple spaces to single space
 * - Remove non-alphanumeric characters except spaces
 */
export function normalizeTitle(title: string): string {
  if (!title) return '';

  let normalized = title
    .toLowerCase()
    .trim()
    // Collapse multiple spaces
    .replace(/\s+/g, ' ')
    // Remove non-alphanumeric except spaces
    .replace(/[^a-z0-9\s]/g, '');

  // Strip leading articles
  normalized = normalized.replace(/^(the|a|an)\s+/, '');

  return normalized.trim();
}

// ============================================================================
// Author Normalization
// ============================================================================

/**
 * Normalize an author name for work group matching.
 * - Lowercase
 * - Trim whitespace
 * - Strip common titles (Dr., Mr., Mrs., Ms., Prof., Sir)
 * - Strip suffixes (Jr., Sr., III, PhD, MD)
 * - Collapse multiple spaces to single space
 */
export function normalizeAuthor(author: string): string {
  if (!author) return 'unknown';

  let normalized = author
    .toLowerCase()
    .trim()
    // Collapse multiple spaces
    .replace(/\s+/g, ' ');

  // Strip common titles at start
  normalized = normalized.replace(/^(dr\.?|mr\.?|mrs\.?|ms\.?|prof\.?|sir)\s+/i, '');

  // Strip common suffixes at end
  normalized = normalized.replace(/\s+(jr\.?|sr\.?|iii|ii|iv|phd|md|esq\.?)$/i, '');

  // Remove periods and other punctuation (e.g., "E.B. White" -> "eb white")
  normalized = normalized.replace(/\./g, '');

  // Collapse any resulting double spaces
  normalized = normalized.replace(/\s+/g, ' ');

  return normalized.trim() || 'unknown';
}

// ============================================================================
// Work Group Generation
// ============================================================================

/**
 * Generate a normalized work_group key from title + author.
 * Groups all editions (hardcover, paperback, board book) of the same work.
 *
 * Uses simple lowercase + trim normalization (matching the existing DB format).
 * For fuzzy matching across variations, use matchesWorkGroup() instead.
 */
export function generateWorkGroup(title: string, author?: string | null): string {
  const normalizedTitle = title.toLowerCase().trim();
  const normalizedAuthor = (author || 'unknown').toLowerCase().trim();
  return `${normalizedTitle}::${normalizedAuthor}`;
}

// ============================================================================
// Work Group Matching
// ============================================================================

/**
 * Check if two books belong to the same work group using deep normalization.
 * More aggressive normalization than generateWorkGroup() -- strips articles,
 * titles, suffixes, and special characters for fuzzy matching.
 *
 * Use this when comparing books that may have slight metadata differences
 * (e.g., "The Cat in the Hat" vs "Cat in the Hat").
 */
export function matchesWorkGroup(
  bookA: { title: string; author: string | null },
  bookB: { title: string; author: string | null },
): boolean {
  const titleA = normalizeTitle(bookA.title);
  const titleB = normalizeTitle(bookB.title);
  const authorA = normalizeAuthor(bookA.author || '');
  const authorB = normalizeAuthor(bookB.author || '');

  return titleA === titleB && authorA === authorB;
}

// ============================================================================
// Binding / Edition Utilities
// ============================================================================

/** Known binding types in the content pipeline */
export const BINDING_TYPES = [
  'Hardcover',
  'Paperback',
  'Board Book',
  'Spiral-bound',
  'Library Binding',
  'Mass Market Paperback',
] as const;

export type BindingType = typeof BINDING_TYPES[number];

/**
 * Normalize a binding string to a known type, or return the original.
 * ISBNdb and other sources may use inconsistent casing/formatting.
 */
export function normalizeBinding(binding: string | null | undefined): string | null {
  if (!binding) return null;

  const lower = binding.toLowerCase().trim();

  // Map common variations to canonical types
  const mappings: Record<string, string> = {
    'hardcover': 'Hardcover',
    'hard cover': 'Hardcover',
    'hardback': 'Hardcover',
    'paperback': 'Paperback',
    'paper back': 'Paperback',
    'softcover': 'Paperback',
    'soft cover': 'Paperback',
    'board book': 'Board Book',
    'board': 'Board Book',
    'boardbook': 'Board Book',
    'spiral-bound': 'Spiral-bound',
    'spiral bound': 'Spiral-bound',
    'spiral': 'Spiral-bound',
    'library binding': 'Library Binding',
    'library': 'Library Binding',
    'mass market paperback': 'Mass Market Paperback',
    'mass market': 'Mass Market Paperback',
  };

  return mappings[lower] || binding;
}

/**
 * Determine whether content can be reused from one edition for another.
 * Text content is typically reusable across editions of the same work,
 * but page counts may differ (board books often have fewer pages).
 *
 * Returns true if the source edition's content is likely compatible.
 */
export function canReuseContent(
  source: { work_group: string | null; quality_status: string | null; digital_page_content: unknown },
  target: { work_group: string | null },
): boolean {
  // Must be the same work
  if (!source.work_group || !target.work_group) return false;
  if (source.work_group !== target.work_group) return false;

  // Source must have usable content
  if (!source.digital_page_content) return false;

  // Source must be in a publishable state
  const usableStatuses = ['ready', 'turn_codes_pending', 'reconstructing'];
  if (!source.quality_status || !usableStatuses.includes(source.quality_status)) return false;

  return true;
}
